import { useState, useEffect, useRef } from "react";
import { FaHeart } from "react-icons/fa";
import './Like.css';
import { toast } from 'react-toastify';
import MatchModal from "../MatchModal/MatchModal";

interface LikeProps {
  likedId: string;
  isLiked?: boolean;
  onLikeChange: (likedId: string, liked: boolean, isMatch?: boolean) => void;
  highlightInitially?: boolean;
}

const Like: React.FC<LikeProps> = ({
  likedId,
  isLiked,
  onLikeChange,
  highlightInitially = false
}) => {
  const [liked, setLiked] = useState<boolean>(isLiked || highlightInitially);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const prevLikedId = useRef<string | null>(null);
  const [showMatchModal, setShowMatchModal] = useState<boolean>(false);
  const [matchedUserName, setMatchedUserName] = useState<string>("");
  const [matchedUserEmail, setMatchedUserEmail] = useState<string>("");

  useEffect(() => {
    const likerId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authToken");

    if (!likerId || !likedId || !authToken) return;
    if (prevLikedId.current === likedId) return;
    prevLikedId.current = likedId;

    const fetchLikeStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(
          `https://nrimarriage.in/api/v1/like/${likerId}/${likedId}/exists`,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${authToken}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: Failed to fetch like status`);
        }

        const data: { hasLiked: boolean } = await response.json();
        setLiked(data.hasLiked);
      } catch (error) {
        console.error("❌ Error fetching like status:", error);
        setError("Failed to load like status. Try again later.");
        toast.error("Failed to load like status.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLikeStatus();
  }, [likedId]);

  const handleLike = async () => {
    const likerId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authToken");

    if (!likerId || !likedId || !authToken) {
      console.error("⚠️ Missing userId or token.");
      toast.error("You must be logged in to like a profile.");
      return;
    }

    const method = liked ? "DELETE" : "POST";
    const apiUrl = `https://nrimarriage.in/api/v1/like/${likerId}/${likedId}`;

    try {
      setIsLoading(true);

      const response = await fetch(apiUrl, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const newLikedStatus = !liked;
      setLiked(newLikedStatus);

      if (newLikedStatus) {
        toast.success("You liked this profile!");

        const mutualResponse = await fetch(
          `https://nrimarriage.in/api/v1/like/${likedId}/${likerId}/exists`,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${authToken}`
            }
          }
        );

        if (mutualResponse.ok) {
          const mutualData: { hasLiked: boolean } = await mutualResponse.json();
          if (mutualData.hasLiked) {
            const userResponse = await fetch(`https://nrimarriage.in/api/v1/users/GetUser?id=${likedId}`, {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${authToken}`
              }
            });

            let matchedName = "Matched User";
            let matchedEmail = "";

            if (userResponse.ok) {
              const userData = await userResponse.json();
              matchedName = userData.firstName || "Matched User";
              matchedEmail = userData.email || "";
            }

            setMatchedUserName(matchedName);
            setMatchedUserEmail(matchedEmail);
            setShowMatchModal(true);
            onLikeChange(likedId, newLikedStatus, true);
          } else {
            onLikeChange(likedId, newLikedStatus, false);
          }
        } else {
          onLikeChange(likedId, newLikedStatus, false);
        }
      } else {
        toast.info("You unliked this profile.");
        onLikeChange(likedId, newLikedStatus, false);
      }
    } catch (error) {
      console.error("❌ Error while sending like request:", error);
      toast.error("Failed to update like status. Please try again.");
      setError("Failed to update like status.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        className={`like-button ${liked ? "liked" : ""} ${highlightInitially ? "highlighted" : ""}`}
        onClick={handleLike}
        disabled={isLoading}
      >
        {isLoading ? "⏳" : <FaHeart size={20} />}
      </button>

      {showMatchModal && (
        <MatchModal
          userName={matchedUserName}
          matchedUserId={likedId}
          matchedUserEmail={matchedUserEmail}
          onClose={() => setShowMatchModal(false)}
          onViewProfile={() => setShowMatchModal(false)}
        />
      )}


    </>
  );
};

export default Like;
